import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { AppState } from 'src/state/store'

export enum FintwinBusinessStepEnum {
  BusinessInfo = 1,
  AccountUse = 2,
  SourceOfFunds = 3,
  // Optional supporting documents, before the final declarations. Declarations
  // shifted 4 -> 5 to make room (mirrors the individual fintwin flow).
  Documents = 4,
  Declarations = 5,
  Completed = 10,
}

export interface FintwinBusinessVerificationState {
  currentStep: FintwinBusinessStepEnum
  availableStep: FintwinBusinessStepEnum
  isCompleted: boolean
  formValues: Record<string, unknown>
  // Id of the user this in-progress questionnaire belongs to. See
  // `claimBusinessOwner` and the mirror note in fintwinVerificationReducer.
  ownerId: number | null
}

export const initialFintwinBusinessVerificationState: FintwinBusinessVerificationState =
  {
    currentStep: FintwinBusinessStepEnum.BusinessInfo,
    availableStep: FintwinBusinessStepEnum.BusinessInfo,
    isCompleted: false,
    formValues: {},
    ownerId: null,
  }

const hydrate = createAction<AppState>(HYDRATE)

const fintwinBusinessVerificationSlice = createSlice({
  name: 'fintwinBusinessVerification',
  initialState: initialFintwinBusinessVerificationState,
  reducers: {
    changeCurrentStep(state, action: PayloadAction<FintwinBusinessStepEnum>) {
      state.currentStep = action.payload
    },
    changeAvailableStep(state, action: PayloadAction<FintwinBusinessStepEnum>) {
      state.availableStep = action.payload
    },
    progressToStep(state, action: PayloadAction<FintwinBusinessStepEnum>) {
      const nextStep = action.payload

      state.currentStep = nextStep

      if (nextStep > state.availableStep) {
        state.availableStep = nextStep
      }
    },
    addFormValues(state, action: PayloadAction<Record<string, unknown>>) {
      state.formValues = { ...state.formValues, ...action.payload }
    },
    completeBusinessVerification(state) {
      state.isCompleted = true
      state.currentStep = FintwinBusinessStepEnum.Completed
      state.availableStep = FintwinBusinessStepEnum.Completed
    },
    // Mirror of claimFintwinOwner: same owner resumes, different/unknown owner
    // gets a clean questionnaire. Guards against cross-user bleed on logout,
    // which no longer wipes this slice.
    claimBusinessOwner(state, action: PayloadAction<number>) {
      if (state.ownerId !== action.payload) {
        return {
          ...initialFintwinBusinessVerificationState,
          ownerId: action.payload,
        }
      }
    },
    flushBusiness() {
      return initialFintwinBusinessVerificationState
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrate, state => state)
  },
})

const { actions, reducer } = fintwinBusinessVerificationSlice

export const {
  changeCurrentStep,
  changeAvailableStep,
  progressToStep,
  addFormValues,
  completeBusinessVerification,
  claimBusinessOwner,
  flushBusiness,
} = actions

export default reducer
